import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Users, Clock, CreditCard, TrendingUp, AlertCircle, Building2 } from "lucide-react";

const MONTHLY_BREAKDOWN_COLORS = {
  "Maintenance Fee": "#7A3A18",
  "Sticker Fee": "#B8714A",
  "Transaction Fee": "#DC2626",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === "security") { navigate("/security", { replace: true }); return; }
    apiGet("/stats").then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div style={{ color: "var(--text2)", fontSize: 13, padding: 20 }}>Loading dashboard...</div>;
  if (!stats) return null;

  if (stats.role === "resident") return <ResidentDashboard stats={stats} user={user} />;
  return <AdminDashboard stats={stats} />;
}

function AdminDashboard({ stats }) {
  const breakdown = stats.monthly_payment_breakdown || {};
  const monthKey = breakdown.month || "";

  const currentYear = new Date().getFullYear();
  const [collectionYear, setCollectionYear] = useState(Math.max(currentYear, 2026));
  const [collection, setCollection] = useState(null);
  const [collectionLoading, setCollectionLoading] = useState(true);
  const [stickerYear, setStickerYear] = useState(Math.max(currentYear, 2026));
  const [stickerCollection, setStickerCollection] = useState(null);
  const [stickerLoading, setStickerLoading] = useState(true);

  const maintenanceRm = monthKey && collection?.month_totals?.[monthKey]
    ? Number(collection.month_totals[monthKey].amount) || 0
    : Number(breakdown.maintenance) || 0;
  const stickerRm = monthKey && stickerCollection?.month_totals?.[monthKey]
    ? Number(stickerCollection.month_totals[monthKey].amount) || 0
    : Number(breakdown.sticker) || 0;
  const transactionRm = monthKey
    && collection?.transaction_fees
    && monthKey in collection.transaction_fees
    && stickerCollection?.transaction_fees
    && monthKey in stickerCollection.transaction_fees
    ? (Number(collection.transaction_fees[monthKey]) || 0)
      + (Number(stickerCollection.transaction_fees[monthKey]) || 0)
    : Number(breakdown.transaction) || 0;

  const pieData = [
    { name: "Maintenance Fee", value: maintenanceRm },
    { name: "Sticker Fee", value: stickerRm },
    { name: "Transaction Fee", value: transactionRm },
  ];
  const chartSlices = pieData.filter(item => item.value > 0);
  const breakdownMonth = monthKey ? fmtMonth(monthKey) : "This month";

  useEffect(() => {
    setCollectionLoading(true);
    apiGet(`/reports/maintenance-collection?year=${collectionYear}`)
      .then(r => setCollection(r.data))
      .catch(() => setCollection(null))
      .finally(() => setCollectionLoading(false));
  }, [collectionYear]);

  useEffect(() => {
    setStickerLoading(true);
    apiGet(`/reports/sticker-collection?year=${stickerYear}`)
      .then(r => setStickerCollection(r.data))
      .catch(() => setStickerCollection(null))
      .finally(() => setStickerLoading(false));
  }, [stickerYear]);

  // Year filter starts from 2026 (first year of digital collection) through at least 2028
  const yearOptions = Array.from(
    { length: Math.max(currentYear, 2028) - 2026 + 1 },
    (_, i) => 2026 + i
  );
  const monthShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const fmtNum = n => Number(n || 0).toLocaleString("en-MY");

  return (
    <div>
      {/* Stats Cards */}
      <div className="stat-cards" data-testid="admin-stat-cards">
        <div className="stat-card" data-testid="stat-residents">
          <div className="card-label">Residents</div>
          <div className="card-value" style={{ color: "var(--brand)" }}>{stats.total_residents}</div>
          <div className="card-delta up"><Users size={11} style={{ display: "inline", marginRight: 3 }} />Active community</div>
        </div>
        <div className="stat-card" data-testid="stat-pending-reg">
          <div className="card-label">Pending Reg.</div>
          <div className="card-value" style={{ color: stats.pending_residents > 0 ? "var(--pending)" : "var(--text)" }}>{stats.pending_residents}</div>
          <div className={`card-delta ${stats.pending_residents > 0 ? "warn" : ""}`}>
            {stats.pending_residents > 0 ? "Needs approval" : "All approved"}
          </div>
        </div>
        <div className="stat-card" data-testid="stat-pending-pay">
          <div className="card-label">Pending Payments</div>
          <div className="card-value" style={{ color: stats.pending_payments > 0 ? "var(--accent)" : "var(--text)" }}>{stats.pending_payments}</div>
          <div className={`card-delta ${stats.pending_payments > 0 ? "warn" : ""}`}>
            {stats.pending_payments > 0 ? "To verify" : "All verified"}
          </div>
        </div>
        <div className="stat-card" data-testid="stat-collection">
          <div className="card-label">Monthly Collection</div>
          <div className="card-value">RM {(stats.monthly_collection / 1000).toFixed(1)}k</div>
          <div className="card-delta up"><TrendingUp size={11} style={{ display: "inline", marginRight: 3 }} />This month</div>
        </div>
        <div className="stat-card" data-testid="stat-outstanding">
          <div className="card-label">Outstanding</div>
          <div className="card-value" style={{ color: stats.outstanding > 0 ? "var(--danger)" : "var(--text)" }}>
            RM {stats.outstanding >= 1000
              ? `${(stats.outstanding / 1000).toFixed(1)}k`
              : stats.outstanding.toFixed(2)}
          </div>
          <div className={`card-delta ${stats.outstanding > 0 ? "warn" : ""}`}>
            <AlertCircle size={11} style={{ display: "inline", marginRight: 3 }} />
            {stats.outstanding > 0 ? "Unpaid maintenance dues" : "None outstanding"}
          </div>
        </div>
      </div>

      {/* Registered residents by street */}
      <div className="section-panel" style={{ marginBottom: 16 }} data-testid="residents-by-street-panel">
        <div className="section-head">
          <div className="section-title">Registered Residents by Street</div>
          <span style={{ fontSize: 11, color: "var(--text2)" }}>{stats.total_residents || 0} total</span>
        </div>
        <div className="summary-grid summary-grid-streets">
          {(stats.residents_by_street || []).map((row) => (
            <div key={row.street} className="summary-grid-item" data-testid={`residents-street-${row.street.replace(/\s|\//g, "-")}`}>
              <div className="card-label">{row.street}</div>
              <div className="summary-grid-val" style={{ color: "var(--brand)" }}>{row.count || 0}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="chart-area" data-testid="admin-charts">
        {/* Bar Chart */}
        <div className="chart-box">
          <div className="chart-title">Monthly Collection (RM)</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={stats.monthly_data || []} barSize={28}>
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "var(--text2)", fontFamily: "var(--mono)" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip
                formatter={v => [`RM ${v.toLocaleString()}`, "Collection"]}
                contentStyle={{ fontSize: 11, background: "#fff", border: "1px solid var(--border)", borderRadius: 6 }}
              />
              <Bar dataKey="amount" fill="var(--brand-mid)" radius={[4,4,0,0]}>
                {(stats.monthly_data || []).map((entry, i) => (
                  <Cell key={i} fill={i === (stats.monthly_data.length - 1) ? "var(--accent)" : "var(--brand-mid)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Donut Chart */}
        <div className="chart-box" data-testid="monthly-payment-breakdown">
          <div className="chart-title">Monthly Payment Breakdown</div>
          <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 8 }}>
            {breakdownMonth} billing month — same as highlighted Jumlah (RM) below
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <ResponsiveContainer width={120} height={120}>
              <PieChart>
                <Pie data={chartSlices.length ? chartSlices : [{ name: "None", value: 1 }]} cx="50%" cy="50%" innerRadius={32} outerRadius={50} dataKey="value" strokeWidth={0}>
                  {(chartSlices.length ? chartSlices : [{ name: "None", value: 1 }]).map((entry) => (
                    <Cell key={entry.name} fill={MONTHLY_BREAKDOWN_COLORS[entry.name] || "#E7E1D8"} />
                  ))}
                </Pie>
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 11, fontWeight: 700, fill: "var(--text)", fontFamily: "var(--font-heading)" }}>
                  {breakdownMonth.split(" ")[0]}
                </text>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              {pieData.map(item => (
                <div key={item.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: MONTHLY_BREAKDOWN_COLORS[item.name], flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 11, color: "var(--text)" }}>{item.name}</span>
                  <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: item.name === "Transaction Fee" ? "var(--danger)" : "var(--text)" }}>
                    {item.name === "Transaction Fee" && item.value > 0
                      ? `−RM ${fmtNum(item.value)}`
                      : `RM ${fmtNum(item.value)}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <CollectionByStreet
        title="Maintenance Fee Collection by Street"
        testId="maintenance-collection"
        year={collectionYear}
        onYearChange={setCollectionYear}
        yearOptions={yearOptions}
        report={collection}
        loading={collectionLoading}
        monthShort={monthShort}
        fmtNum={fmtNum}
        highlightMonth={monthKey}
        amountHint="Street cells are payment counts. Highlighted Jumlah (RM) matches Maintenance Fee above."
      />

      <CollectionByStreet
        title="Sticker Fee Collection by Street"
        testId="sticker-collection"
        year={stickerYear}
        onYearChange={setStickerYear}
        yearOptions={yearOptions}
        report={stickerCollection}
        loading={stickerLoading}
        monthShort={monthShort}
        fmtNum={fmtNum}
        highlightMonth={monthKey}
        amountHint="Street cells are payment counts. Highlighted Jumlah (RM) matches Sticker Fee above."
      />
    </div>
  );
}

function CollectionByStreet({ title, testId, year, onYearChange, yearOptions, report, loading, monthShort, fmtNum, highlightMonth, amountHint }) {
  const colStyle = (m, extra) => ({
    textAlign: "right",
    background: m === highlightMonth ? "rgba(122, 58, 24, 0.08)" : undefined,
    ...extra,
  });
  return (
    <div className="section-panel" style={{ marginBottom: 16 }} data-testid={`${testId}-panel`}>
      <div className="section-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="section-title">{title}</div>
          {amountHint && (
            <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 4 }}>{amountHint}</div>
          )}
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--text2)" }}>
          Year
          <select
            className="form-input"
            style={{ fontSize: 11, height: 28, width: "auto", padding: "2px 8px" }}
            value={year}
            onChange={e => onYearChange(Number(e.target.value))}
            data-testid={`${testId}-year`}
          >
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Loading collection data...</div>
      ) : !report ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Unable to load collection data</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="percasa-table" data-testid={`${testId}-table`}>
            <thead>
              <tr>
                <th>Street</th>
                {(report.months || []).map((m, i) => (
                  <th
                    key={m}
                    style={{
                      textAlign: "right",
                      color: m === highlightMonth ? "var(--brand)" : undefined,
                      background: m === highlightMonth ? "rgba(122, 58, 24, 0.08)" : undefined,
                    }}
                  >
                    {monthShort[i] || m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.rows.map(row => (
                <tr key={row.street}>
                  <td style={{ fontWeight: 500, whiteSpace: "nowrap" }}>{row.street}</td>
                  {(report.months || []).map(m => (
                    <td key={m} style={colStyle(m)}>
                      <span className="mono" style={{ fontSize: 11, fontWeight: 600 }}>
                        {fmtNum(row.months?.[m]?.count)}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
              <tr style={{ background: "var(--bg2, #faf7f4)" }}>
                <td style={{ fontWeight: 700 }}>Jumlah (Count)</td>
                {(report.months || []).map(m => (
                  <td key={m} style={colStyle(m, { fontWeight: 700 })}>
                    <span className="mono" style={{ fontSize: 11 }}>{fmtNum(report.month_totals?.[m]?.count)}</span>
                  </td>
                ))}
              </tr>
              <tr style={{ background: "var(--bg2, #faf7f4)" }}>
                <td style={{ fontWeight: 700 }}>Jumlah (RM)</td>
                {(report.months || []).map(m => (
                  <td key={m} style={colStyle(m, { fontWeight: 700, boxShadow: m === highlightMonth ? "inset 0 -2px 0 var(--brand)" : undefined })}>
                    <span className="mono" style={{ fontSize: 11, color: "var(--success)" }}>{fmtNum(report.month_totals?.[m]?.amount)}</span>
                  </td>
                ))}
              </tr>
              <tr style={{ background: "var(--bg2, #faf7f4)" }}>
                <td style={{ fontWeight: 700 }}>Transaction Fee (RM)</td>
                {(report.months || []).map(m => {
                  const fee = Number(report.transaction_fees?.[m] || 0);
                  return (
                    <td key={m} style={colStyle(m, { fontWeight: 700 })}>
                      <span className="mono" style={{ fontSize: 11, color: fee > 0 ? "var(--danger)" : "var(--text2)" }}>
                        {fee > 0 ? `-${fmtNum(fee)}` : "0"}
                      </span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResidentDashboard({ stats, user }) {
  const outstanding = stats.outstanding || { months_count: 0, total_amount: 0, months: [] };
  const hasOutstanding = outstanding.months_count > 0;

  return (
    <div className="resident-dashboard">
      <div className="res-summary-cards" data-testid="resident-stat-cards">
        <div className="res-card">
          <div className="rc-label">My House No.</div>
          <div className="rc-val rc-val-mono" style={{ color: "var(--brand)" }}>
            {stats.unit || "—"}
          </div>
        </div>
        <div className="res-card">
          <div className="rc-label">Last Payment</div>
          <div className="rc-val rc-val-sm">
            {stats.last_payment ? `${fmtMonth(stats.last_payment.month)} ✓` : "No payments yet"}
          </div>
        </div>
        <div className="res-card" data-testid="resident-outstanding-summary">
          <div className="rc-label">Total Outstanding</div>
          <div className="rc-val rc-val-mono" style={{ color: hasOutstanding ? "var(--danger)" : "var(--success)" }}>
            RM {outstanding.total_amount.toFixed(2)}
          </div>
          <div className="rc-hint">
            {outstanding.months_count} {outstanding.months_count === 1 ? "month" : "months"} outstanding
          </div>
        </div>
        <div className="res-card">
          <div className="rc-label">Account Status</div>
          <div className="rc-val rc-val-sm">
            <span className={`badge ${stats.status}`}>{capitalize(stats.status)}</span>
          </div>
        </div>
      </div>

      <div className="section-panel" style={{ marginBottom: 16 }} data-testid="resident-outstanding-panel">
        <div className="section-head">
          <div className="section-title">Payment Outstanding</div>
        </div>
        {!hasOutstanding ? (
          <div className="empty-panel-msg">
            You have no outstanding payments. All dues are up to date.
          </div>
        ) : (
          <div className="table-scroll table-scroll-compact">
            <table className="percasa-table">
              <thead>
                <tr><th>Month</th><th>Amount</th><th>Status</th></tr>
              </thead>
              <tbody>
                {outstanding.months.map((item, i) => (
                  <tr key={`${item.month}-${i}`}>
                    <td><span className="mono">{fmtMonth(item.month)}</span></td>
                    <td><span className="mono">RM {item.amount?.toFixed(2)}</span></td>
                    <td><span className={`badge ${item.status}`}>{capitalize(item.status)}</span></td>
                  </tr>
                ))}
                <tr>
                  <td style={{ fontWeight: 600 }}>Total ({outstanding.months_count} {outstanding.months_count === 1 ? "month" : "months"})</td>
                  <td colSpan={2}><span className="mono" style={{ fontWeight: 600, color: "var(--danger)" }}>RM {outstanding.total_amount.toFixed(2)}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="section-panel" data-testid="resident-summary">
        <div className="section-head">
          <div className="section-title">Payment Summary</div>
        </div>
        <div className="summary-grid">
          {[
            { label: "Total Submissions", val: stats.total_payments, color: "var(--text)" },
            { label: "Pending Verification", val: stats.pending_count, color: stats.pending_count > 0 ? "var(--pending)" : "var(--text)" },
            { label: "Unread Notifications", val: stats.unread_notifications, color: stats.unread_notifications > 0 ? "var(--accent)" : "var(--text)" },
          ].map(item => (
            <div key={item.label} className="summary-grid-item">
              <div className="card-label">{item.label}</div>
              <div className="summary-grid-val" style={{ color: item.color }}>{item.val || 0}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function fmtMonth(m) {
  if (!m) return "—";
  const [yr, mo] = m.split("-");
  const labels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${labels[parseInt(mo,10)-1]} ${yr}`;
}
function capitalize(s) {
  if (!s) return "";
  if (s === "unpaid") return "Unpaid";
  return s.charAt(0).toUpperCase() + s.slice(1);
}
